import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { companiesAPI } from "../../lib/api";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import NotificationBell from "../NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  LayoutDashboard,
  Users,
  FileText,
  Package,
  Kanban,
  ClipboardList,
  TextCursor,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Building2,
  ChevronDown,
  UserCircle,
  Palette,
  FolderKanban,
  Receipt,
  Check,
  Activity,
  TrendingUp,
  Truck,
  CreditCard,
  FileSignature,
  Mail,
  Calendar,
  Upload,
  Flame,
  Cable
} from "lucide-react";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/kunden", label: "Kunden", icon: Users },
  { path: "/termine", label: "Termine", icon: Calendar, roles: ["superadmin", "admin"] },
  { path: "/projekte", label: "Projekte", icon: FolderKanban },
  { path: "/angebote", label: "Angebote", icon: FileText },
  { path: "/wp-angebote", label: "WP-Angebote", icon: Flame, roles: ["superadmin", "admin"] },
  { path: "/angebote-import", label: "PDF Import", icon: Upload, roles: ["superadmin", "admin"] },
  { path: "/reonic-import", label: "Reonic Import", icon: Cable, roles: ["superadmin", "admin"] },
  { path: "/email-csv-import", label: "Lead-Import", icon: Mail, roles: ["superadmin", "admin"] },
  { path: "/rechnungen", label: "Rechnungen", icon: Receipt, roles: ["superadmin", "admin"] },
  { path: "/lieferanten", label: "Lieferanten", icon: Truck, roles: ["superadmin", "admin"] },
  { path: "/gutschriften", label: "Gutschriften", icon: CreditCard, roles: ["superadmin", "admin"] },
  { path: "/signaturen", label: "Signaturen", icon: FileSignature, roles: ["superadmin", "admin"] },
  { path: "/einstellungen/email-tracking", label: "E-Mail-Status", icon: Mail, roles: ["superadmin", "admin"] },
  { path: "/planung", label: "Planung", icon: ClipboardList },
  { path: "/pipeline", label: "Pipeline", icon: Kanban },
  { path: "/produkte", label: "Produkte", icon: Package },
  { path: "/margen", label: "Margen", icon: TrendingUp, roles: ["superadmin", "admin"] },
  { path: "/firmen", label: "Firmen", icon: Building2, roles: ["superadmin"] },
  { path: "/firmen-ci", label: "Firmen CI", icon: Palette, roles: ["superadmin", "admin"] },
  { path: "/benutzer", label: "Benutzer", icon: UserCircle, roles: ["superadmin", "admin"] },
  { path: "/textbausteine", label: "Textbausteine", icon: TextCursor },
  { path: "/aktivitaetsprotokoll", label: "Protokoll", icon: Activity, roles: ["superadmin", "admin"] },
];

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const { user, logout, selectCompany, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  // Load companies for the user
  useEffect(() => {
    const loadCompanies = async () => {
      if (user?.company_ids?.length > 0 || user?.role === "superadmin") {
        try {
          const response = await companiesAPI.list();
          const allCompanies = response.data;
          
          // Filter to only user's companies (unless superadmin)
          if (user.role === "superadmin") {
            setCompanies(allCompanies);
          } else {
            setCompanies(allCompanies.filter(c => user.company_ids?.includes(c.id)));
          }
          
          // Set active company
          if (user.active_company_id) {
            const active = allCompanies.find(c => c.id === user.active_company_id);
            setActiveCompany(active);
          }
        } catch (error) {
          console.error("Failed to load companies:", error);
        }
      }
    };
    loadCompanies();
  }, [user?.company_ids, user?.active_company_id, user?.role]);

  const handleCompanySwitch = async (companyId) => {
    if (companyId !== user?.active_company_id) {
      await selectCompany(companyId);
      const newActive = companies.find(c => c.id === companyId);
      setActiveCompany(newActive);
    }
  };

  const canSwitchCompanies = user?.role === "superadmin" || (user?.company_ids?.length > 1);

  const filteredNavItems = navItems.filter(item => 
    !item.roles || item.roles.includes(user?.role)
  );

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path || 
      (item.path !== "/" && location.pathname.startsWith(item.path));
    
    return (
      <NavLink
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 touch-target ${
          isActive 
            ? "bg-primary text-primary-foreground" 
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`}
        data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
      >
        <item.icon className="h-5 w-5" />
        <span className="font-medium">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-6 w-6" />
        </Button>
        
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center">
            <Sun className="h-4 w-4 text-white" />
          </div>
          <span className="font-bold text-lg font-['Manrope']">SolarCRM</span>
        </div>
        
        <div className="flex items-center gap-1">
          <NotificationBell token={token} />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle-mobile"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 bg-card border-r border-border z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center">
                <Sun className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg font-['Manrope']">SolarCRM</h1>
                <p className="text-xs text-muted-foreground">PV Angebote & Verträge</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Company Selector */}
          {(user?.active_company_id || (user?.role === "superadmin" && companies.length > 0) || (user?.company_ids?.length > 0)) && (
            <div className="p-4 border-b border-border">
              {canSwitchCompanies && companies.length > 0 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="w-full justify-between px-3 py-2 h-auto bg-muted hover:bg-muted/80"
                      data-testid="company-switcher-btn"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm font-medium truncate">
                          {activeCompany?.name || "Firma auswählen"}
                        </span>
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuLabel>Firma wechseln</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {companies.map((company) => (
                      <DropdownMenuItem 
                        key={company.id}
                        onClick={() => handleCompanySwitch(company.id)}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="truncate">{company.name}</span>
                          {company.id === user?.active_company_id && (
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0 ml-2" />
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : activeCompany ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium truncate">
                    {activeCompany?.name || "Aktive Firma"}
                  </span>
                </div>
              ) : null}
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 p-4">
            <nav className="space-y-2">
              {filteredNavItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </nav>
          </ScrollArea>

          {/* User Menu */}
          <div className="p-4 border-t border-border">
            {/* Notification Bell for Desktop */}
            <div className="hidden lg:flex justify-end mb-3">
              <NotificationBell token={token} />
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start gap-3 h-auto py-3"
                  data-testid="user-menu-btn"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {user?.role}
                    </p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mein Konto</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <NavLink to="/profil" className="flex items-center">
                    <UserCircle className="mr-2 h-4 w-4" />
                    <span>Mein Profil</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
                  {theme === "dark" ? (
                    <>
                      <Sun className="mr-2 h-4 w-4" />
                      <span>Helles Design</span>
                    </>
                  ) : (
                    <>
                      <Moon className="mr-2 h-4 w-4" />
                      <span>Dunkles Design</span>
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NavLink to="/einstellungen" className="flex items-center">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Einstellungen</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout}
                  className="text-destructive focus:text-destructive"
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Abmelden</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
